import React, { useEffect, useState } from "react";

type WidgetSettings = {
  launcher: {
    iconUrl: string;
    iconFit?: "cover" | "contain";
    sizeVariant: "small" | "normal" | "large";
    primaryColor: string;
    shape: "circle" | "rounded" | "square";
    shadow: boolean;
    position: "bottom-right" | "bottom-left" | "top-right" | "top-left";
    animation: "none" | "bounce" | "pulse" | "spin" | "spin-reverse";
  };
  window: {
    header: {
      title: string;
      background: string;
      titleColor: string;
    };
    input: {
      placeholder: string;
      bg: string;
      text: string;
      border: string;
    };
    sendButton: {
      bg: string;
      text: string;
    };
    message: {
      fontSize: number;
      userBubble: { bg: string; text: string };
      botBubble: { bg: string; text: string };
    };
  };
  advanced: {
    window: { width: number; height: number };
  };
};

const DEFAULT_SETTINGS: WidgetSettings = {
  launcher: {
    iconUrl: "",
    iconFit: "cover",
    sizeVariant: "normal",
    primaryColor: "#667eea",
    shape: "circle",
    shadow: true,
    position: "bottom-right",
    animation: "none",
  },
  window: {
    header: {
      title: "How can we help?",
      background: "#667eea",
      titleColor: "#ffffff",
    },
    input: {
      placeholder: "Type your message...",
      bg: "#ffffff",
      text: "#111827",
      border: "#e5e7eb",
    },
    sendButton: {
      bg: "#667eea",
      text: "#ffffff",
    },
    message: {
      fontSize: 14,
      userBubble: { bg: "#eef2ff", text: "#111827" },
      botBubble: { bg: "#f1f5f9", text: "#111827" },
    },
  },
  advanced: {
    window: { width: 360, height: 520 },
  },
};

interface WidgetCustomizationBuilderProps {
  value?: Partial<WidgetSettings> | null;
  onChange?: (next: WidgetSettings) => void;
}

function mergeSettings(
  base: WidgetSettings,
  incoming: Partial<WidgetSettings> | null | undefined
): WidgetSettings {
  const b = base;
  const v = incoming || {};
  return {
    launcher: {
      iconUrl: v.launcher?.iconUrl ?? b.launcher.iconUrl,
      iconFit: v.launcher?.iconFit ?? b.launcher.iconFit ?? "cover",
      sizeVariant: v.launcher?.sizeVariant ?? b.launcher.sizeVariant,
      primaryColor: v.launcher?.primaryColor ?? b.launcher.primaryColor,
      shape: v.launcher?.shape ?? b.launcher.shape,
      shadow: v.launcher?.shadow ?? b.launcher.shadow,
      position: v.launcher?.position ?? b.launcher.position,
      animation: v.launcher?.animation ?? b.launcher.animation,
    },
    window: {
      header: {
        title: v.window?.header?.title ?? b.window.header.title,
        background: v.window?.header?.background ?? b.window.header.background,
        titleColor: v.window?.header?.titleColor ?? b.window.header.titleColor,
      },
      input: {
        placeholder: v.window?.input?.placeholder ?? b.window.input.placeholder,
        bg: v.window?.input?.bg ?? b.window.input.bg,
        text: v.window?.input?.text ?? b.window.input.text,
        border: v.window?.input?.border ?? b.window.input.border,
      },
      sendButton: {
        bg: v.window?.sendButton?.bg ?? b.window.sendButton.bg,
        text: v.window?.sendButton?.text ?? b.window.sendButton.text,
      },
      message: {
        fontSize: v.window?.message?.fontSize ?? b.window.message.fontSize,
        userBubble: {
          bg:
            v.window?.message?.userBubble?.bg ?? b.window.message.userBubble.bg,
          text:
            v.window?.message?.userBubble?.text ??
            b.window.message.userBubble.text,
        },
        botBubble: {
          bg: v.window?.message?.botBubble?.bg ?? b.window.message.botBubble.bg,
          text:
            v.window?.message?.botBubble?.text ??
            b.window.message.botBubble.text,
        },
      },
    },
    advanced: {
      window: {
        width: v.advanced?.window?.width ?? b.advanced.window.width,
        height: v.advanced?.window?.height ?? b.advanced.window.height,
      },
    },
  };
}

const WidgetCustomizationBuilder: React.FC<WidgetCustomizationBuilderProps> = ({
  value,
  onChange,
}) => {
  const [settings, setSettings] = useState<WidgetSettings>(() =>
    mergeSettings(DEFAULT_SETTINGS, value)
  );
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("launcher");

  useEffect(() => {
    const merged = mergeSettings(DEFAULT_SETTINGS, value);
    // Avoid clobbering local edits if props round-trip equal
    try {
      if (JSON.stringify(merged) !== JSON.stringify(settings)) {
        setSettings(merged);
      }
    } catch {
      setSettings(merged);
    }
  }, [value, settings]);

  const updateSetting = (path: string, value: any) => {
    setSettings((prev) => {
      const newSettings = { ...prev } as any;
      const keys = path.split(".");
      let current: any = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        current[k] = { ...(current[k] || {}) };
        current = current[k];
      }
      current[keys[keys.length - 1]] = value;
      if (onChange) {
        try {
          onChange(newSettings as WidgetSettings);
        } catch {}
      }
      return newSettings as WidgetSettings;
    });
  };

  const getLauncherSize = () => {
    switch (settings.launcher.sizeVariant) {
      case "small":
        return 48;
      case "large":
        return 72;
      default:
        return 60;
    }
  };

  const getLauncherRadius = () => {
    switch (settings.launcher.shape) {
      case "square":
        return "8px";
      case "rounded":
        return "16px";
      default:
        return "50%";
    }
  };

  const getPosition = () => {
    const pos = settings.launcher.position;
    const style: React.CSSProperties = { position: "absolute" };

    if (pos.includes("bottom")) style.bottom = "16px";
    if (pos.includes("top")) style.top = "16px";
    if (pos.includes("right")) style.right = "16px";
    if (pos.includes("left")) style.left = "16px";

    return style;
  };

  const tabs = [
    { id: "launcher", label: "Launcher" },
    { id: "header", label: "Header" },
    { id: "input", label: "Input" },
    { id: "messages", label: "Messages" },
    { id: "advanced", label: "Advanced" },
  ];

  return (
    <div
      className="widget-customization"
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0px",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.15fr",
          gap: "30px",
        }}
      >
        {/* Settings Panel */}
        <div>
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              marginBottom: "20px",
              borderBottom: "1px solid var(--wc-border)",
              overflowX: "auto",
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "12px 16px",
                  border: "none",
                  background: activeTab === tab.id ? "#3b82f6" : "transparent",
                  color: activeTab === tab.id ? "white" : "var(--wc-text-muted)",
                  borderRadius: "8px 8px 0 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form Content */}
          <div
            style={{
              background: "var(--wc-panel)",
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid var(--wc-border)",
              minHeight: "400px",
            }}
          >
            {activeTab === "launcher" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Position
                  </label>
                  <select
                    value={settings.launcher.position}
                    onChange={(e) =>
                      updateSetting("launcher.position", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  >
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="top-left">Top Left</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Size
                  </label>
                  <select
                    value={settings.launcher.sizeVariant}
                    onChange={(e) =>
                      updateSetting("launcher.sizeVariant", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  >
                    <option value="small">Small</option>
                    <option value="normal">Normal</option>
                    <option value="large">Large</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Shape
                  </label>
                  <select
                    value={settings.launcher.shape}
                    onChange={(e) =>
                      updateSetting("launcher.shape", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  >
                    <option value="circle">Circle</option>
                    <option value="rounded">Rounded</option>
                    <option value="square">Square</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Logo
                  </label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={settings.launcher.iconUrl}
                    onChange={(e) =>
                      updateSetting("launcher.iconUrl", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Logo Fit
                  </label>
                  <select
                    value={settings.launcher.iconFit}
                    onChange={(e) =>
                      updateSetting("launcher.iconFit", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  >
                    <option value="cover">Cover</option>
                    <option value="contain">Contain</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Primary Color
                  </label>
                  <input
                    type="color"
                    value={settings.launcher.primaryColor}
                    onChange={(e) =>
                      updateSetting("launcher.primaryColor", e.target.value)
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Animation
                  </label>
                  <select
                    value={settings.launcher.animation}
                    onChange={(e) =>
                      updateSetting("launcher.animation", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  >
                    <option value="none">None</option>
                    <option value="bounce">Bounce</option>
                    <option value="pulse">Pulse</option>
                    <option value="spin">Spin</option>
                    <option value="spin-reverse">Spin Reverse</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={settings.launcher.shadow}
                      onChange={(e) =>
                        updateSetting("launcher.shadow", e.target.checked)
                      }
                    />
                    <span>Shadow</span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === "header" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    value={settings.window.header.title}
                    onChange={(e) =>
                      updateSetting("window.header.title", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Background Color
                  </label>
                  <input
                    type="color"
                    value={settings.window.header.background}
                    onChange={(e) =>
                      updateSetting("window.header.background", e.target.value)
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Text Color
                  </label>
                  <input
                    type="color"
                    value={settings.window.header.titleColor}
                    onChange={(e) =>
                      updateSetting("window.header.titleColor", e.target.value)
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === "input" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Placeholder
                  </label>
                  <input
                    type="text"
                    value={settings.window.input.placeholder}
                    onChange={(e) =>
                      updateSetting("window.input.placeholder", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                      color: "var(--wc-text-primary)",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Send Button Color
                  </label>
                  <input
                    type="color"
                    value={settings.window.sendButton.bg}
                    onChange={(e) =>
                      updateSetting("window.sendButton.bg", e.target.value)
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === "messages" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Font Size: {settings.window.message.fontSize}px
                  </label>
                  <input
                    type="range"
                    min="12"
                    max="18"
                    value={settings.window.message.fontSize}
                    onChange={(e) =>
                      updateSetting(
                        "window.message.fontSize",
                        Number(e.target.value)
                      )
                    }
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    User Bubble Color
                  </label>
                  <input
                    type="color"
                    value={settings.window.message.userBubble.bg}
                    onChange={(e) =>
                      updateSetting(
                        "window.message.userBubble.bg",
                        e.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Bot Bubble Color
                  </label>
                  <input
                    type="color"
                    value={settings.window.message.botBubble.bg}
                    onChange={(e) =>
                      updateSetting(
                        "window.message.botBubble.bg",
                        e.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "6px",
                      border: "1px solid var(--wc-border)",
                      background: "var(--wc-control-bg)",
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === "advanced" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Width: {settings.advanced.window.width}px
                  </label>
                  <input
                    type="range"
                    min="300"
                    max="500"
                    value={settings.advanced.window.width}
                    onChange={(e) =>
                      updateSetting(
                        "advanced.window.width",
                        Number(e.target.value)
                      )
                    }
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                    }}
                  >
                    Height: {settings.advanced.window.height}px
                  </label>
                  <input
                    type="range"
                    min="400"
                    max="600"
                    value={settings.advanced.window.height}
                    onChange={(e) =>
                      updateSetting(
                        "advanced.window.height",
                        Number(e.target.value)
                      )
                    }
                    style={{ width: "100%" }}
                  />
                </div>

                <button
                  onClick={() => setSettings(DEFAULT_SETTINGS)}
                  style={{
                    padding: "12px 24px",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  Reset to Defaults
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div style={{ position: "sticky", top: "20px" }}>
          <div
            style={{
              background: "var(--wc-panel)",
              borderRadius: "12px",
              border: "1px solid var(--wc-border)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                padding: "16px",
                color: "white",
                fontWeight: "600",
              }}
            >
              Live Preview
            </div>

            <div
              style={{
                padding: "20px",
                background: "var(--wc-body)",
                minHeight: `${settings.advanced.window.height + 180}px`,
                position: "relative",
              }}
            >
              {/* Launcher Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                  ...getPosition(),
                  width: `${getLauncherSize()}px`,
                  height: `${getLauncherSize()}px`,
                  borderRadius: getLauncherRadius(),
                  background: settings.launcher.primaryColor,
                  border: "none",
                  color: "white",
                  fontSize: "20px",
                  cursor: "pointer",
                  boxShadow: settings.launcher.shadow
                    ? "0 8px 25px rgba(0,0,0,0.15)"
                    : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation:
                    settings.launcher.animation === "bounce"
                      ? "bounce 1s infinite"
                      : settings.launcher.animation === "pulse"
                        ? "pulse 2s infinite"
                        : settings.launcher.animation === "spin"
                          ? "spin 1s infinite"
                          : settings.launcher.animation === "spin-reverse"
                            ? "spin reverse 1s infinite"
                            : "none",
                }}
              >
                {settings.launcher.iconUrl ? (
                  <img
                    src={settings.launcher.iconUrl}
                    alt="launcher-icon"
                    style={{
                      width: `${getLauncherSize() - 20}px`,
                      height: `${getLauncherSize() - 20}px`,
                      objectFit: settings.launcher.iconFit || "cover",
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                )}
              </button>

              {/* Chat Window */}
              {isOpen && (
                <div
                  style={{
                    ...getPosition(),
                    width: `clamp(280px, ${settings.advanced.window.width}px, calc(100% - 32px))`,
                    minHeight: `${settings.advanced.window.height}px`,
                    height: `${settings.advanced.window.height}px`,
                    background: "var(--wc-panel)",
                    borderRadius: "16px",
                    boxShadow: "var(--wc-shadow)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      background: settings.window.header.background,
                      color: settings.window.header.titleColor,
                      padding: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 14,
                          background: settings.launcher.primaryColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {settings.launcher.iconUrl ? (
                          <img
                            src={settings.launcher.iconUrl}
                            alt="logo"
                            style={{
                              width: 18,
                              height: 18,
                              objectFit: settings.launcher.iconFit || "cover",
                              borderRadius: 6,
                            }}
                          />
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          </svg>
                        )}
                      </div>
                      {settings.window.header.title}
                    </span>
                    <button
                      onClick={() => setIsOpen(false)}
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        color: settings.window.header.titleColor,
                        width: "24px",
                        height: "24px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {/* Messages */}
                  <div
                    style={{
                      flex: 1,
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        alignSelf: "flex-start",
                        background: settings.window.message.botBubble.bg,
                        color: settings.window.message.botBubble.text,
                        padding: "10px 12px",
                        borderRadius: "16px 16px 16px 4px",
                        fontSize: `${settings.window.message.fontSize}px`,
                        maxWidth: "80%",
                      }}
                    >
                      Hello! How can I help you today?
                    </div>
                    <div
                      style={{
                        alignSelf: "flex-end",
                        background: settings.window.message.userBubble.bg,
                        color: settings.window.message.userBubble.text,
                        padding: "10px 12px",
                        borderRadius: "16px 16px 4px 16px",
                        fontSize: `${settings.window.message.fontSize}px`,
                        maxWidth: "80%",
                      }}
                    >
                      I want to customize this widget!
                    </div>
                  </div>

                  {/* Input */}
                  <div
                    style={{
                      padding: "12px",
                      borderTop: "1px solid var(--wc-border)",
                      display: "flex",
                      gap: "8px",
                    }}
                  >
                    <input
                      placeholder={settings.window.input.placeholder}
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        border: "1px solid var(--wc-border)",
                        borderRadius: "12px",
                        outline: "none",
                        fontSize: `${settings.window.message.fontSize}px`,
                        background: "var(--wc-control-bg)",
                        color: "var(--wc-text-primary)",
                      }}
                    />
                    <button
                      style={{
                        background: settings.window.sendButton.bg,
                        color: settings.window.sendButton.text,
                        border: "none",
                        padding: "10px 16px",
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      Send
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          /* Theme tokens for widget customization builder */
          .widget-customization {
            --wc-body: #f8fafc;
            --wc-panel: #ffffff;
            --wc-border: #e5e7eb;
            --wc-text-primary: #0f172a;
            --wc-text-secondary: #334155;
            --wc-text-muted: #64748b;
            --wc-control-bg: #f9fafb;
            --wc-shadow: 0 20px 60px rgba(0,0,0,0.15);
          }
          [data-bs-theme="dark"] .widget-customization {
            --wc-body: #0f172a;
            --wc-panel: #0b1220;
            --wc-border: #334155;
            --wc-text-primary: #f8fafc;
            --wc-text-secondary: #cbd5e1;
            --wc-text-muted: #94a3b8;
            --wc-control-bg: #111827;
            --wc-shadow: 0 24px 60px rgba(0,0,0,0.45);
          }
        `,
        }}
      />
    </div>
  );
};

export default WidgetCustomizationBuilder;
